import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Line } from 'react-chartjs-2';
import { theme } from 'theme';
import { Box, Grid } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import { fetchCreditReviewChartsData } from 'services';
import { Loader } from 'ui/Loader/Loader';
import clsx from 'clsx';

const chartOptions = {
  scales: {
    xAxes: [
      {
        gridLines: {
          display: false,
        },
        ticks: {
          padding: 20,
          fontSize: 16,
          fontStyle: 'bold',
        },
      },
    ],
    yAxes: [
      {
        gridLines: {
          borderDash: [10, 5],
          color: theme.palette.grey[400],
          drawBorder: false,
        },
        ticks: {
          min: 0,
          max: 10,
          padding: 20,
          fontSize: 16,
          fontStyle: 'bold',
          callback: value => value.toFixed(1),
        },
      },
    ],
  },
  layout: {
    padding: {
      top: 50,
      bottom: 20,
      right: 50,
      left: 20,
    },
  },
  legend: {
    display: false,
  },
};

const useStyles = makeStyles({
  legend: {
    marginTop: theme.spacing(1),
    fontSize: 18,
  },
  point: {
    backgroundColor: theme.palette.grey[400],
  },
  entity: {
    fontWeight: 'bold',
  },
  entityPoint: {
    backgroundColor: theme.palette.custom.moderateBlue,
  },
});

const Legend = ({ entityName, industry }) => {
  const classes = useStyles();

  return (
    <Box mb={2}>
      <Grid container className={classes.legend} direction="row" justifyContent="center" spacing={4}>
        <Grid item className={classes.entity}>
          <Box
            position="relative"
            width={20}
            height={20}
            marginRight={1}
            top={3}
            display="inline-block"
            borderRadius="20px"
            className={clsx(classes.point, classes.entityPoint)}
          />
          {entityName}
        </Grid>
        {industry && (
          <Grid item>
            <Box
              position="relative"
              width={20}
              height={20}
              marginRight={1}
              top={3}
              display="inline-block"
              borderRadius="20px"
              className={classes.point}
            />
            {industry}
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

Legend.defaultProps = {
  industry: null,
};

Legend.propTypes = {
  entityName: PropTypes.string.isRequired,
  industry: PropTypes.string,
};

const ScoreHistoryWidget = ({ easyNumber, entityName }) => {
  const { t } = useTranslation();
  const [isPending, setIsPending] = useState(false);
  const [chartData, setChartData] = useState({
    labels: [],
    entityValues: [],
    industryValues: [],
  });

  useEffect(() => {
    const fetchData = async () => {
      setIsPending(true);

      const data = await fetchCreditReviewChartsData(easyNumber);

      if (data) {
        setChartData({
          labels: data.score_over_time_labels,
          entityValues: data.score_over_time_values,
          industryValues: data.score_over_time_peer_median,
        });
      }

      setIsPending(false);
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { labels, entityValues, industryValues } = chartData;

  const data = canvas => {
    const ctx = canvas.getContext('2d');
    const gradient = ctx.createLinearGradient(0, 0, 0, 500);

    gradient.addColorStop(0, 'rgba(230, 230, 230, 1)');
    gradient.addColorStop(1, 'rgba(230, 230, 230, 0)');

    return {
      labels,
      datasets: [
        {
          label: entityName,
          borderColor: theme.palette.custom.moderateBlue,
          data: entityValues,
          fill: true,
          borderWidth: 5,
          pointRadius: 7,
          pointBorderColor: theme.palette.custom.moderateBlue,
          pointBackgroundColor: 'white',
          pointBorderWidth: 3,
          backgroundColor: gradient,
        },
        {
          label: `${t('Peers')} (${t('median')})`,
          fill: true,
          borderColor: theme.palette.grey[400],
          data: industryValues,
          borderWidth: 5,
          pointRadius: 7,
          pointBorderColor: theme.palette.grey[400],
          pointBackgroundColor: 'white',
          pointBorderWidth: 3,
          backgroundColor: gradient,
        },
      ],
    };
  };

  return (
    <Box mt={2} mx={2}>
      {isPending ? <Loader /> : <Line options={chartOptions} data={data} />}
      <Legend entityName={entityName} industry={t('Peers')} />
    </Box>
  );
};

ScoreHistoryWidget.propTypes = {
  easyNumber: PropTypes.string.isRequired,
  entityName: PropTypes.string.isRequired,
};

export default ScoreHistoryWidget;
